import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class LeroyRobinson extends BaseTestScenario {
	public get maxScore(): number {
		return 53100;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var prePoints = 1000;
		var awardedPoints = 32700;

		return assessPatientRemoveDenturesPoints + awardedPoints +
			setupFiberOpticsPoints + prePoints;
    }

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 11;
    }

	public getDefaultApneaTime(): number {
		return 210
	}

	public getDefaultSa02(): number {
		return 94
	}
	
	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);
		
		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				"Due to stridor caused by a pharyngeal tumor or abscess, bag valve mask ventilation is unlikely to be successful in maintaining oxygenation.",
                "In the cases with fully obstructive pharyngeal tumor or abscess any attempt of airway instrumentation will likely induce massive bleeding and/or release of purulent material and immediate airway obstruction.",
                "Did you factor in the clinical condition of the patient in your decision to not utilize multiple techniques and optimizations?"
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished() : void  {
		this._CheckOxy();
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'In the cases with fully obstructive pharyngeal tumor or abscess any attempt of airway instrumentation will likely induce massive bleeding and/or release of purulent material and immediate airway obstruction. Mr. Robinson has expired. Review the debrief and then try again.');
	}

	public onSGAFinished() : void  {
		this._CheckOxy();
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'In the cases with fully obstructive pharyngeal tumor or abscess any attempt of airway instrumentation will likely induce massive bleeding and/or release of purulent material and immediate airway obstruction. Mr. Robinson has expired. Review the debrief and then try again.');
	}

	public onCricFinished() : void  {
		this.cricAttemptCount = 1;
		this._CheckOxy();
		if(!this.isLite) {
			this.addScore(this.predeterminedPoints) // If this is the full version, points added here. If lite, points added after they select 'yes' to having the ooram make the incision (cric config part 9)
		}
		this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'You successfully performed a cricothyrotomy and established a stable, patent airway for Leroy Robinson ' + this.apneaTimeElapsed + ' after he became apneic. A team arrives shortly thereafter to establish a definitive airway for Mr. Robinson. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points.');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData() : AssessAirwayActionData[]  {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'You can palpate the thyroid and cricoid cartilage as well as the cricothyroid membrane.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 3 finger breadths (4 cm) <br /> Hyoid to Thyroid Notch Distance: 1 finger breadth (2 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'No feedback to display'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient is not wearing dentures'
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'No feedback to display'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 2 finger breadths (3 cm)',
				imgUrl:'./assets/images/assess-airway-images/OpenMouthDrool.png'
			}
		]
    }

	private _setupIntro () {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Leroy Robinson. He’s a 70-year-old male, 72 in, 80 kg. 
		// 			His temperature is 40 degrees Celsius. His potassium is 3.8 and he has 
		// 			been unable to eat anything for the last three days because of a sore 
		// 			throat. He also has diabetes and presented today because of difficulty breathing.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario10 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			The patient now has stridor and a likely pharyngeal abscess. I’ll manage the
		// 				hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario10 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.Leroyrobinson,
		// 			text: `
						
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario10 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Leroy Robinson came in with difficulty breathing. His temperature is 40 degrees Celsius. He is here because of a severe sore throat that has progressed to difficulty breathing and he has developed stridor. He is already receiving epinephrine and steroids.`,
		// 		b: `
		// 			Age: 70<br />
		// 			Height (in): 72 in<br />
		// 			Weight (kg): 80 kg<br />
		// 			Potassium (K): 3.79<br />
		// 			NPO Status: He has had nothing to eat or drink for more than 8 hours<br />
		// 			<br />
		// 			PMH: Diabetes
		// 		`,
		// 		a: `Patient has an upper respiratory tract infection causing odynophagia, dyspnea, and progressive stridor.`,
		// 		r: `The physician needs you to manage the airway for impending respiratory collapse.`
		// 	}
		// }

		this._id_ScenarioSet(10); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario10 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Leroy Robinson came in with difficulty breathing. His temperature is 40 degrees Celsius. He is here because of a severe sore throat that has progressed to difficulty breathing and he has developed stridor. He is already receiving epinephrine and steroids.`,
				b: `
					Age: 70<br />
					Height (in): 72 in<br />
					Weight (kg): 80 kg<br />
					Potassium (K): 3.79<br />
					NPO Status: He has had nothing to eat or drink for more than 8 hours<br />
					<br />
					PMH: Diabetes
				`,
				b508: `
					Age. 70.
					Height. 72 inches.
					Weight. 80 kilograms.
					Potassium. 3.79.
					NPO Status. He has had nothing to eat or drink for more than 8 hours
					Past Medical History. Diabetes.
				`,
				a: `Patient has an upper respiratory tract infection causing odynophagia, dyspnea, and progressive stridor.`,
				r: `The physician needs you to manage the airway for impending respiratory collapse.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario10 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			},
			isCric: true
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario10 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario10 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario10 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario10 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario10 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario10 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario10 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			waitForPreOxygenateVideoChapter: () =>this._id_SegmentAssembly(this._id_preOxText),//'DAARC - Scenario10 - intro - 53',
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection);
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),//'DAARC - Scenario10 - intro - 53',
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),//'DAARC - Scenario10 - intro - 53',
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade1Abscess.png';
				},
				magillForceps: {
					text: () => 'Don’t touch the abscess. It will bleed.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
				this._CheckOxy();
				this.showFailedScenarioPopUp("In the cases with fully obstructive pharyngeal tumor or abscess any attempt of airway instrumentation will likely induce massive bleeding and/or release of purulent material and immediate airway obstruction. Mr. Robinson has expired. Review the debrief and then try again.","Scenario Failed")
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_SGAAttempt()),//'DAARC - Scenario10 - intro - 53',
			insertingSgaVideoChapter: () => this._id_SegmentAssembly(this._id_SGAAttempt()),//'DAARC - Scenario10 - intro - 53',
			presetupText: 'Touching the abscess will make it bleed.',
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
				this._CheckOxy();
				this.showFailedScenarioPopUp("In the cases with fully obstructive pharyngeal tumor or abscess any attempt of airway instrumentation will likely induce massive bleeding and/or release of purulent material and immediate airway obstruction. Mr. Robinson has expired. Review the debrief and then try again.","Scenario Failed")
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		}

		this.procedures.cric.config.isCricScenario = true;

		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]			
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat'),//'DAARC - Scenario10 - intro - 53'
		};
		this.procedures.cric.config.part9Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			onYesClicked: () => {
				this.addScore(1200) // lite cric points that are normally awarded during cric
				this.addScore(this.predeterminedPoints); // successful attempt points
			},
			onNoClicked: () => {
				this.debriefLogFailedItem('You did not have the OORAM perform the incision')
				this.showFailedScenarioPopUp('You absolutely need to have the level 3 OORAM provider perform the cric. Review the debrief and then try again.','Failed Scenario')
			}
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario10/daarc-scenario10-leroyrobinson-v1.mp4',
			bvmConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),//'DAARC - Scenario10 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo
				}
            },
            ettConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),//'DAARC - Scenario10 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo
				}
            },
            sgaConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),//'DAARC - Scenario10 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo
				}
            },
            cricConfig: {
                moveToAuscultateScreenVideoChapter: () =>this._id_SegmentAssembly(this._id_cricAusc, 'flat'),//'DAARC - Scenario10 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathYes
				}
			}
		};
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 130,
			bloodPressureDenominator: 80,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 94,
			heartRate: 80,
			heartRateMax: 200,
			respirationRate: 32,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
            onInductionTimerRanOut: () => {
                context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Leroy Robinson. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}